import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { BattleLogEvent } from "../types";
import { getBuffIconPath } from "../utils/buffs";

export interface BuffFlyAnimation {
  id: string;
  icon: string;
  from: "hero-center" | "hero-bar" | "enemy";
  to: "hero-bar" | "enemy";
  enemyUid?: string;
}

function iconForEffect(effectId: string): string {
  if (effectId === "bleed") return "bleed";
  if (effectId === "red_potion_atk") return "red_potion";
  return effectId;
}

export function useBuffFlyAnimations(visibleLog: BattleLogEvent[]): BuffFlyAnimation[] {
  const prevLenRef = useRef(0);
  const [animations, setAnimations] = useState<BuffFlyAnimation[]>([]);

  useEffect(() => {
    const newEvents = visibleLog.slice(prevLenRef.current);
    prevLenRef.current = visibleLog.length;
    if (!newEvents.length) return;

    const spawned: BuffFlyAnimation[] = [];
    for (const ev of newEvents) {
      if (ev.action !== "BUFF_APPLY" || !ev.effect_id || !ev.target) continue;

      const icon = iconForEffect(ev.effect_id);
      const id = `${ev.turn}-${ev.actor}-${ev.target}-${ev.effect_id}`;

      if (ev.actor.startsWith("player") && ev.target.startsWith("player")) {
        spawned.push({ id, icon, from: "hero-center", to: "hero-bar" });
      } else if (ev.actor.startsWith("player")) {
        spawned.push({ id, icon, from: "hero-bar", to: "enemy", enemyUid: ev.target });
      } else {
        spawned.push({ id, icon, from: "enemy", to: "hero-bar", enemyUid: ev.actor });
      }
    }

    if (spawned.length) {
      setAnimations((prev) => [...prev, ...spawned]);
      const ids = new Set(spawned.map((a) => a.id));
      setTimeout(() => setAnimations((prev) => prev.filter((a) => !ids.has(a.id))), 700);
    }
  }, [visibleLog]);

  return animations;
}

interface BuffFlyOverlayProps {
  animations: BuffFlyAnimation[];
  enemyUids: string[];
}

export function BuffFlyOverlay({ animations, enemyUids }: BuffFlyOverlayProps) {
  if (!animations.length) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-50 overflow-hidden">
      {animations.map((anim, index) => {
        const enemyIndex = anim.enemyUid ? enemyUids.indexOf(anim.enemyUid) : -1;
        const enemyX =
          enemyIndex >= 0
            ? `${12 + (enemyIndex / Math.max(enemyUids.length, 1)) * 76}%`
            : "50%";

        let className = "absolute w-8 h-8 animate-buff-to-hero-bar";
        let style: CSSProperties = { left: "50%", top: "55%" };

        if (anim.from === "hero-center" && anim.to === "hero-bar") {
          className = "absolute w-9 h-9 animate-buff-self";
          style = { left: "50%", top: "45%", transform: "translate(-50%, -50%)" };
        } else if (anim.from === "hero-bar" && anim.to === "enemy") {
          className = "absolute w-8 h-8 animate-buff-to-enemy";
          style = { left: "50%", top: "78%", ["--enemy-x" as string]: enemyX };
        } else if (anim.from === "enemy" && anim.to === "hero-bar") {
          className = "absolute w-8 h-8 animate-buff-enemy-to-hero";
          style = { left: enemyX, top: "18%" };
        } else {
          className = "absolute w-8 h-8 animate-buff-to-hero-bar";
          style = { left: "50%", top: "55%" };
        }

        return (
          <img
            key={`${anim.id}-${index}`}
            src={getBuffIconPath(anim.icon)}
            alt=""
            className={className}
            style={style}
          />
        );
      })}
    </div>
  );
}
